import React from 'react';
import { Controller, type Control, type UseFormRegister, type UseFormSetValue } from 'react-hook-form';
import { UnitToggle } from '../../../components/common/UnitToggle';
import { WeightInput } from '../../../components/common/WeightInput';
import { formatRupiah, parseRupiah } from '../../../utils/format';
import type { InventoryItemFormData } from '../../../types/inventory';
import type { WeightUnit } from '../../../hooks/useWeightUnit';
import { useLanguage } from '../../../features/language/useLanguage';

interface InventoryStockCostSectionProps {
  isWeightBased: boolean;
  unit: WeightUnit;
  onToggleUnit: () => void;
  control: Control<InventoryItemFormData>;
  register: UseFormRegister<InventoryItemFormData>;
  standardCost: number;
  setValue: UseFormSetValue<InventoryItemFormData>;
}

export const InventoryStockCostSection: React.FC<InventoryStockCostSectionProps> = ({
  isWeightBased,
  unit,
  onToggleUnit,
  control,
  register,
  standardCost,
  setValue,
}) => {
  const { t } = useLanguage();
  return (
    <div className="bg-white shadow rounded-lg p-6 space-y-6" data-test-id="inventory-stock-cost">
      <h2 className="text-lg font-medium text-gray-900 border-b pb-2">{t('inventory.stockCost.title')}</h2>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <div className="flex justify-between items-center">
            <label className="block text-sm font-medium text-gray-700">{t('inventory.lotDetail.fields.initialQuantity')}</label>
            {isWeightBased && <UnitToggle unit={unit} onToggle={onToggleUnit} />}
          </div>
          {isWeightBased ? (
            <Controller
              control={control}
              name="quantity"
              render={({ field: { value, onChange } }) => (
                <WeightInput
                  value={value}
                  onChange={onChange}
                  unit={unit}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
                  placeholder={t('inventory.history.placeholderKg')}
                  data-test-id="inventory-create-quantity"
                />
              )}
            />
          ) : (
            <input
              type="number"
              step="0.001"
              min="0"
              inputMode="decimal"
              {...register('quantity', { valueAsNumber: true })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
              data-test-id="inventory-create-quantity"
            />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">{t('inventory.modal.fields.standardCost')}</label>
          <input
            type="text"
            value={formatRupiah(standardCost)}
            onChange={event => {
              const raw = parseRupiah(event.target.value);
              setValue('standardCost', raw, { shouldValidate: true });
            }}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
            data-test-id="inventory-create-cost"
          />
        </div>

        <div>
          <div className="flex justify-between items-center">
            <label className="block text-sm font-medium text-gray-700">{t('inventory.stockCost.reorderWarning')}</label>
            {isWeightBased && <UnitToggle unit={unit} onToggle={onToggleUnit} />}
          </div>
          {isWeightBased ? (
            <Controller
              control={control}
              name="reorderPoint"
              render={({ field: { value, onChange } }) => (
                <WeightInput
                  value={value}
                  onChange={onChange}
                  unit={unit}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
                  placeholder={t('inventory.history.placeholderKg')}
                  data-test-id="inventory-create-reorder-point"
                />
              )}
            />
          ) : (
            <input
              type="number"
              step="0.001"
              min="0"
              inputMode="decimal"
              {...register('reorderPoint', { valueAsNumber: true })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
              data-test-id="inventory-create-reorder-point"
            />
          )}
        </div>

        <div>
          <div className="flex justify-between items-center">
            <label className="block text-sm font-medium text-gray-700">{t('inventory.stockCost.reorderQuantity')}</label>
            {isWeightBased && <UnitToggle unit={unit} onToggle={onToggleUnit} />}
          </div>
          {isWeightBased ? (
            <Controller
              control={control}
              name="reorderQuantity"
              render={({ field: { value, onChange } }) => (
                <WeightInput
                  value={value}
                  onChange={onChange}
                  unit={unit}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
                  placeholder={t('inventory.history.placeholderKg')}
                  data-test-id="inventory-create-reorder-quantity"
                />
              )}
            />
          ) : (
            <input
              type="number"
              step="0.001"
              min="0"
              inputMode="decimal"
              {...register('reorderQuantity', { valueAsNumber: true })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
              data-test-id="inventory-create-reorder-quantity"
            />
          )}
        </div>
      </div>
    </div>
  );
};
